/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import uniqBy from 'lodash.uniqby';
import moment from 'moment';
import dynamic from 'next/dynamic';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {
  HISTORY_MIN_DAYS,
  TIME_MODE_ASSET_DAY_LIMIT,
  TIMELINE_LIMIT_DAYS
} from '@ion/dls/src/maps/components/timeMode/current/constants';
import {
  FLEET_ASSET_LAYER_ID,
  FLEET_SELECTED_ASSET_LAYER_ID,
  IDENTIFIER_PROPERTY,
  LIVE_HISTORY_LAYER_ID
} from '@ion/dls/src/maps/constants';
import {CURRENT_LAYER} from '@ion/dls/src/maps/layers/CurrentLayerSettings';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import {
  HistoryReportTypeEnum,
  TimelineViewBy,
  TimeModeLimitAction,
  TimeModeLimitErrorType,
  TimeModes,
  TrackingMode
} from '@ion/dls/src/types/types';
import {openTrackingFilterAtCategory} from '@/components/filter/current/store';
import {TrackingFilterDrawerCategoryId} from '@/components/filter/current/types';
import {getTrackingFilterCountKey} from '@/components/filter/current/useTrackingFilterLimitWarning';
import {
  getFleetDefinitionExpression,
  getRunningTimeModeReport,
  isVicinityHistoryReport,
  toRangeDays
} from '@/routes/tracking2/helpers';
import useFilteredAssetIds from '@/routes/tracking2/hooks/grid/useFilteredAssetIds';
import {
  applyAssetHistoryDateFilter
} from '@/store/actions/reports';
import {
  cancelLiveHistory,
  runLiveHistory,
  setHistoryActive,
  setHistoryFrom,
  setHistoryPreset,
  setHistoryTo,
  setLiveHistoryAssetSearchList,
  setLiveHistoryFetching,
  setLiveHistoryInitialAssets,
  setLiveHistoryInputEvents,
  setLiveHistoryReportType,
  setLiveHistorySearchTime,
  setMapWidgetSelected,
  setTimelineDuration,
  setTimelinePreset,
  setTimeMode,
  setTimeModePanelOpen,
  setTrackingMapLayers,
  setTrackingMode
} from '@/store/actions/tracking';
import {
  globalGroupIdSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {
  boundaryFilterSelector,
  boundaryModeSelector,
  historyActiveSelector,
  historyFromSelector,
  historyPresetSelector,
  historyToSelector,
  isTimeModePanelOpenSelector,
  liveHistoryActiveSelector,
  liveHistoryInputEventsSelector,
  liveHistoryReportTypeSelector,
  mapLayerSelector,
  mapViewSelector,
  timelineDurationSelector,
  timelinePresetSelector,
  timeModeSelector,
  trackingFiltersSelector,
  trackingGridStateSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {useReturnToLiveTracking} from '../../../../tracking/hooks/history/useReturnToLiveTracking';
import {useSyncLegacyTimeMode} from '../../../../tracking/hooks/history/useSyncLegacyTimeMode';
import {acceptHistoryReport, setTimeModeLayerCount, setTimeModeReportStale} from '../store';

const DynamicTimeModePanelContainer = dynamic(
  () => import('@ion/dls/src/maps/components/timeMode/current/TimeModePanelContainer'),
  {ssr: false}
);

// the panel's View By and Live History's report type are one reading under
// two names: Asset against Asset, and the panel's Input against the report's
// Event - the input events the panel's own legend names. Mapped across
// rather than shared outright, so neither side has to speak the other's
// vocabulary ...
const toViewBy = (reportType: HistoryReportTypeEnum): TimelineViewBy => (
  reportType === HistoryReportTypeEnum.Event ? TimelineViewBy.input : TimelineViewBy.asset
);

const toReportType = (viewBy: TimelineViewBy): HistoryReportTypeEnum => (
  viewBy === TimelineViewBy.input ? HistoryReportTypeEnum.Event : HistoryReportTypeEnum.Asset
);

// the span the report is actually run over. The panel drafts its chips and
// its custom selector apart, so both come back from it - a lit chip carries
// the span, and the selector is left holding whatever it was last set to.
// Which of the two the user meant is told by the chip: `preset` is null
// exactly when Custom is the lit one, and only then does the selector speak
// for the span. Both are read in hours, which is what Live History searches
// by, so neither needs converting ...
const toSearchTime = (preset: number | null, duration: number): number => preset ?? duration;

// the two fleet layers a timeline reports over - the assets drawn on the map
// and the ones the user has picked out of them ...
const FLEET_LAYER_IDS = [FLEET_ASSET_LAYER_ID, FLEET_SELECTED_ASSET_LAYER_ID];

// every feature a layer holds under the where clause given. The clause is
// built from the tracking filters as applied - the same one the Fleet layer
// draws with - rather than read back off the layer's view, which trails the
// filter by an effect and, while a timeline runs, also leaves that
// timeline's own assets out. Built from the filters, the query is always
// the Groups / Assets tree's selection exactly, whatever order the filter
// and the report were set in ...
const queryFleetFeatures = (
  layer: __esri.FeatureLayer,
  where: string
): Promise<__esri.Graphic[]> => {
  const query = layer.createQuery();
  query.where = where;
  return layer.queryFeatures(query).then(({features}) => features);
};

// how many distinct assets the fleet layers hold between them - an asset
// picked out onto the selected layer is counted once, whichever of the two
// layers it is read off ...
const countAssets = (features: __esri.Graphic[]): number => (
  new Set(features.map(({attributes}) => attributes?.[IDENTIFIER_PROPERTY])).size
);

// what a count that failed reads as - neither no assets nor any over the
// limit, so it lets the panel through rather than refusing it either way ...
const UNCOUNTED_ASSETS = Number.NaN;

// the fewest days each mode can report over, which is what a switch into it
// from Live is checked against before its panel opens ...
const MIN_MODE_DAYS: Partial<Record<TimeModes, number>> = {
  [TimeModes.timeline] : TIMELINE_LIMIT_DAYS,
  [TimeModes.history]  : HISTORY_MIN_DAYS
};

// a History selection the limit refused, held with the figures it was
// refused over, so the error can report them and Change Date Range can
// reopen the panel on the very range the user picked ...
type RejectedHistory = {
  selection: {preset: any; from: Date; to: Date};
  assets: number;
  days: number;
};

// a running report the filter was changed under, but which the limit kept
// from being re-run - held with the figures it was refused over, so the
// error can report them ...
type StaleReport = {
  mode: TimeModes.timeline | TimeModes.history;
  assets: number;
  days: number;
};

// the fleet features a timeline searches over, keyed the way Live History
// addresses its assets ...
const toSearchAssets = (features: __esri.Graphic[]) => (
  features.map(function({attributes}) {
    return {...attributes, id: attributes[IDENTIFIER_PROPERTY]};
  })
);

// the live history layer joins whatever is already drawn, without doubling
// itself where a previous run left it there - and the Fleet layer steps
// aside for it, the same way returning to Live steps the Live History layer
// aside for the Fleet layer back. The report reads over the assets' history
// rather than their current position, so the two are never shown together ...
const withLiveHistoryLayer = (layers: string[]): string[] => {
  const withoutCurrentLayer = layers?.filter(layerId => layerId !== CURRENT_LAYER) || [];
  return withoutCurrentLayer.includes(LIVE_HISTORY_LAYER_ID)
    ? withoutCurrentLayer
    : [...withoutCurrentLayer, LIVE_HISTORY_LAYER_ID];
};

/**
* @TimeModePanelContainer
*
* Defines a container to wrap the panel the map's active time mode runs with
* - the Timeline panel while the map is in its Timeline mode, and in time the
* History panel while it is in History. Live is a mode with no panel of its
* own.
*
* ```typescript
*
*   import TimeModePanelContainer from './widgets/TimeModePanelContainer';
*
*   <TimeModePanelContainer />
*
* ```
*/
const TimeModePanelContainer = ({view}: MapContainerProps) => {
  const dispatch                = useDispatch();
  const isMounted               = useHasMounted();
  const trackingMode            = useSelector(trackingModeSelector);
  const timeMode                = useSelector(timeModeSelector);
  // the span the panel's Custom selector opens on, which is its own reading
  // rather than the one the report runs over - the selector holds the span
  // it was last set to even while a chip is lit over it ...
  const timelineDuration        = useSelector(timelineDurationSelector);
  // which chip the panel opens lit, `null` where Custom is the live choice
  // and the span above is what the report runs over instead ...
  const timelinePreset          = useSelector(timelinePresetSelector);
  // History's own last-applied preset and resolved range - `null` until it
  // has been applied at least once, which leaves the panel's own default to
  // take over instead of forcing one here ...
  const historyPreset           = useSelector(historyPresetSelector);
  const historyFrom             = useSelector(historyFromSelector);
  const historyTo               = useSelector(historyToSelector);
  // which way the panel colours its paths, which Live History already keeps
  // as the type of report it runs ...
  const viewByReportType        = useSelector(liveHistoryReportTypeSelector);
  // the layers already drawn, which the live history layer is added to ...
  const layers                  = useSelector(mapLayerSelector);
  const inputEventList          = useSelector(liveHistoryInputEventsSelector);
  // the Live map's own view, kept apart from the `view` prop above - this
  // panel is mounted on two different maps (the Live map and the
  // AssetHistory report's own), and a Timeline run always reads the Fleet
  // layers off the Live map regardless of which of the two hosts it,
  // since only that map ever has them drawn ...
  const liveMapView              = useSelector(mapViewSelector);

  // the mode says which panel belongs on screen, but not whether it is still
  // there - applying a timeline leaves the mode running and puts the panel
  // away, and re-selecting that mode asks for it back without moving the
  // mode at all. The store carries that reading of its own ...
  const isPanelOpen             = useSelector(isTimeModePanelOpenSelector);
  const globalGroupId           = useSelector(globalGroupIdSelector);
  // which mode the control itself reads - the same flags `useMapToolProps`
  // lights its pill off - so a Timeline opened from Live can be told apart
  // from one reopened over a timeline already running ...
  const isLiveHistoryActive     = useSelector(liveHistoryActiveSelector);
  const isHistoryActive         = useSelector(historyActiveSelector);
  // a vicinity search applied in the current theme - History over it is
  // bounded by the vicinity rather than by the fleet on the map, so none of
  // History's limit checks below are held against it. Read for History
  // whatever the mode now reads, so a History panel asked for over it is let
  // through the same as the report already running ...
  const boundaryMode            = useSelector(boundaryModeSelector);
  const boundary                = useSelector(boundaryFilterSelector);
  const isVicinityApplied       = isVicinityHistoryReport({
    runningReport : TimeModes.history,
    boundaryMode,
    boundary
  });
  // how many assets are on the map as a mode's panel is asked for, `null`
  // until they have been counted ...
  const [assetCount, setAssetCount] = useState<number | null>(null);
  // a History selection refused on Apply, `null` while there is none ...
  const [rejectedHistory, setRejectedHistory] = useState<RejectedHistory | null>(null);
  // the refused range Change Date Range reopens the panel on, `null` once
  // it has been applied or put away ...
  const [historyDraft, setHistoryDraft] = useState<RejectedHistory['selection'] | null>(null);
  // a running report the limit kept from being re-run under a changed
  // filter, `null` while there is none ...
  const [staleReport, setStaleReport] = useState<StaleReport | null>(null);
  // the tracking filters as applied - a deep-equal selector, so it only
  // changes when what is filtered actually does ...
  const filters                 = useSelector(trackingFiltersSelector);
  const {filterApplied}         = useSelector(trackingGridStateSelector);
  const filteredIds             = useFilteredAssetIds();
  const returnToLiveTracking    = useReturnToLiveTracking();
  // an Asset History or Live History report run in the legacy theme, which
  // has no Time Mode control, is picked up as History's or Timeline's own
  // once the current theme reads it ...
  useSyncLegacyTimeMode();

  // what the map was in before, so the effect below can tell a move into
  // Live from merely sitting in it ...
  const previousTimeModeRef      = useRef(timeMode);
  // switching accounts is itself a move back to Live, wherever it happens -
  // this panel is only ever mounted while on Tracking or the AssetHistory
  // report to begin with, and a Timeline/History report run over one
  // account's assets has nothing to say once the map reads another's. The
  // effect below treats a changed group the same as the control's own Live
  // pill rather than leaving a stale report running under it ...
  const previousGlobalGroupIdRef = useRef(globalGroupId);
  // the Timeline's input events are only given up once its report actually
  // stops. Picking another mode's pill merely asks for that mode's panel -
  // the Timeline carries on running under it - so clearing the inputs there
  // re-drew every line of a report still on screen. The pill leaving the
  // Timeline marks them for clearing instead, and whichever route really
  // tears the Timeline down clears them ...
  const pendingInputClearRef     = useRef(false);

  // every asset on both fleet layers. The two are queried together rather
  // than one after the other - neither depends on the other's answer - and a
  // layer the map has not drawn yet is simply passed over ...
  // the filter as applied, as the Fleet layer draws it - both fleet layers
  // are queried under it, so an asset picked out on the map but outside the
  // tree's selection is not reported over either ...
  const fleetWhere = useMemo(() => (
    getFleetDefinitionExpression(filters, filteredIds, filterApplied)
  ), [filters, filteredIds, filterApplied]);
  // the clause a running report was last run - or refused - under, so a
  // change to what is on the map can be told from a render that changed
  // nothing. A string, so it only differs when the assets it selects do ...
  //
  // a boundary or vicinity search narrows the report as well, though the
  // fleet clause does not carry it - so drawing, redrawing or clearing one
  // re-runs the report the same way ...
  const reportScope            = useMemo(() => (
    `${fleetWhere}|${JSON.stringify(boundary ?? [])}`
  ), [fleetWhere, boundary]);
  const previousReportScopeRef = useRef(reportScope);

  // an asset picked out on the map can be held by both layers at once, so
  // the two are merged by asset rather than simply joined ...
  const getFleetAssets = useCallback(async(): Promise<__esri.Graphic[]> => {
    const fleetLayers = FLEET_LAYER_IDS
      .map(layerId => liveMapView?.map?.findLayerById(layerId) as __esri.FeatureLayer)
      .filter(Boolean);
    const features = await Promise.all(
      fleetLayers.map(layer => queryFleetFeatures(layer, fleetWhere))
    );
    return uniqBy(
      features.reduce((all, layerFeatures) => [...all, ...layerFeatures], []),
      ({attributes}) => attributes?.[IDENTIFIER_PROPERTY]
    );
  }, [liveMapView, fleetWhere]);

  // a Timeline or History panel is held back until the assets it would
  // report over are counted, and refused outright where they already exceed
  // the limit over the fewest days that mode can span - one, for either.
  // That holds whichever mode the map is in when the panel is asked for:
  // from Live as much as over a report already running, whose filter may
  // since have been changed to more assets than it could be re-run over ...
  const isModeRequested = isPanelOpen && timeMode !== TimeModes.live;
  const minModeDays     = MIN_MODE_DAYS[timeMode] ?? HISTORY_MIN_DAYS;
  const isAwaitingCount = isModeRequested && assetCount === null;
  const exceedsLimit    = isModeRequested &&
    !(timeMode === TimeModes.history && isVicinityApplied) &&
    assetCount !== null &&
    assetCount * minModeDays > TIME_MODE_ASSET_DAY_LIMIT;
  // no assets match the filters, so the mode asked for has nothing to report
  // over - from Live, or over a report already running ...
  const hasNoAssets     = isModeRequested && assetCount === 0;
  // the mode the control reads - the one the refusal offers to stay in. The
  // same flags `useMapToolProps` lights its pill off ...
  const controlMode     = isLiveHistoryActive && trackingMode === TrackingMode.Live
    ? TimeModes.timeline
    : isHistoryActive && trackingMode === TrackingMode.History
      ? TimeModes.history
      : TimeModes.live;
  // the longest range History may run over the assets counted, which the
  // panel disables every wider preset against ...
  const maxHistoryDays  = assetCount ? Math.floor(TIME_MODE_ASSET_DAY_LIMIT / assetCount) : undefined;

  // counted afresh each time the panel is asked for, since the map may be
  // showing a different set of assets than it was the last time. A count
  // that fails lets the panel through rather than holding it back for good,
  // the same as a map with no fleet layers drawn yet ...
  useEffect(() => {
    let isCancelled = false;
    if (isModeRequested) {
      getFleetAssets()
        .then(countAssets)
        .catch(() => UNCOUNTED_ASSETS)
        .then(function(count) {
          if (!isCancelled) {
            setAssetCount(count);
          }
        });
    } else {
      setAssetCount(null);
    }
    return () => {
      isCancelled = true;
    };
  }, [isModeRequested, getFleetAssets]);

  // a refusal only answers for the panel it was raised over - once that is
  // put away, by whatever route, it has nothing left to report ...
  useEffect(() => {
    if (!isPanelOpen) {
      setRejectedHistory(null);
      setHistoryDraft(null);
    }
  }, [isPanelOpen]);

  // every way out of the limit error leaves the map in Live, the same way
  // dismissing the Timeline panel itself does ...
  const onLimitErrorDismiss = useCallback(() => {
    setRejectedHistory(null);
    dispatch(setTimeModePanelOpen(false));
  }, []);

  // Change Date Range puts the History panel back in the error's place, on
  // the range that was refused rather than the last one applied - the panel
  // is still open underneath, so clearing the refusal is all it takes. The
  // selection is kept aside for the panel to reopen on ...
  const onChangeDateRange = useCallback(() => {
    setHistoryDraft(rejectedHistory?.selection ?? null);
    setRejectedHistory(null);
  }, [rejectedHistory]);

  // Refine Filter leaves the map in Live too, and hands the user the tracking
  // filter opened at Groups / Assets - the category that decides which
  // assets are on the map, and so how many the limit is counted over ...
  const onLimitErrorRefineFilter = useCallback(() => {
    setRejectedHistory(null);
    dispatch(setTimeModePanelOpen(false));
    openTrackingFilterAtCategory(TrackingFilterDrawerCategoryId.GroupsAssets);
  }, []);

  // what the user settled on is kept, so the next open comes back to the
  // same chip, the same Custom span and the same view. Three readings go
  // down rather than two: the chip, the span the selector was left holding,
  // and - as the interval Live History searches by - the span the report is
  // actually run over, which is whichever of the two the chip points at.
  // Keeping the selector's own reading beside it is what lets a chip be lit
  // over a differently set Custom without the selector losing its place.
  // Only applying reaches here: abandoning the panel leaves the stored
  // selection as it was ...
  const persistSelection = useCallback(({preset, duration, viewBy, inputEvents}) => {
    dispatch(setTimelinePreset(preset));
    dispatch(setTimelineDuration(duration));
    dispatch(setLiveHistorySearchTime(toSearchTime(preset, duration)));
    dispatch(setLiveHistoryReportType(toReportType(viewBy)));
    dispatch(setLiveHistoryInputEvents(inputEvents));
  }, []);

  // runs a timeline over the fleet features given - the snapshot it reports
  // over, then the report itself. Shared by Apply and by a re-run under a
  // changed filter, which already has the live history layer drawn ...
  const runTimeline = useCallback((features: __esri.Graphic[]) => {
    const assets = toSearchAssets(features);
    dispatch(setLiveHistoryAssetSearchList(assets, true));
    dispatch(setLiveHistoryInitialAssets(assets));
    dispatch(runLiveHistory());
  }, []);

  // applying is what actually runs the timeline: the selection is kept, the
  // assets it reports over are gathered off the map, the live history layer
  // is drawn, and the report goes. The selection is kept first so the run
  // reads the span and view the user just settled on.
  //
  // History is marked inactive here too - picking Timeline's pill and
  // applying it straight from an already-running History report leaves
  // nothing to tear History back down otherwise, and the two reading active
  // together would light both chips in the applied filter bar at once.
  //
  // trackingMode is moved to Live here too - this panel is mounted on both
  // the Live map host and the AssetHistory report's own map host, and
  // applying Timeline from the report is itself a request to leave it and
  // watch the report run on the Live map instead, the same page Timeline
  // already shows on when applied from there directly ...
  const onTimelineApply = useCallback(async(selection) => {
    dispatch(setTimeModePanelOpen(false));
    dispatch(setHistoryActive(false));
    dispatch(setTrackingMode(TrackingMode.Live) as any);
    persistSelection(selection);
    const features = await getFleetAssets();
    dispatch(setTrackingMapLayers(withLiveHistoryLayer(layers)));
    setTimeModeReportStale(false);
    runTimeline(features);
  }, [layers, persistSelection, getFleetAssets, runTimeline]);

  // Cancel and the X are one act: the panel goes away and nothing else
  // moves. The mode is left as it is, so the control still reads Timeline
  // and whatever a previous report drew stays on the map - going back to
  // Live is something the user asks for on the control itself, and the
  // effect below is what answers it. The draft the panel was holding is
  // given up, which the panel does for itself, so nothing is written here:
  // a selection is only kept by applying it ...
  const onPanelDismiss = useCallback(() => {
    if (controlMode !== TimeModes.live && controlMode !== timeMode) {
      dispatch(setTimeMode(controlMode));
    }
    dispatch(setTimeModePanelOpen(false));
  }, [controlMode, timeMode]);

  const onTimelineDismiss = onPanelDismiss;

  // the History panel's Cancel and its own X are the same act as Timeline's -
  // the panel goes away and the mode is left as it was, so History stays the
  // control's reading and Live is only reached from the control itself ...
  const onHistoryDismiss = onPanelDismiss;

  // applying keeps the selection the same way Timeline's own Apply does -
  // the preset and the range it resolved to - so the next open reads back
  // whatever was last applied rather than History's own default. It also
  // marks History active, the same way Timeline's own Apply marks its
  // report active - which is what tells the filter bar's chip a selection
  // was actually applied rather than only drafted.
  //
  // Timeline's own run is cancelled here too, the same reasoning in reverse -
  // picking History's pill and applying it straight from an already-running
  // Timeline leaves nothing to tear Timeline back down otherwise. Only a
  // selection the limit has let through reaches here ...
  const applyHistory = useCallback(({preset, from, to}) => {
    setHistoryDraft(null);
    if (pendingInputClearRef.current) {
      pendingInputClearRef.current = false;
      dispatch(setLiveHistoryInputEvents([]));
    }
    dispatch(setTimeModePanelOpen(false));
    dispatch(setHistoryPreset(preset));
    dispatch(setHistoryFrom(from));
    dispatch(setHistoryTo(to));
    dispatch(setHistoryActive(true));
    dispatch(cancelLiveHistory());
    dispatch(setTrackingMode(TrackingMode.History) as any);
    dispatch(applyAssetHistoryDateFilter({
      fromDate      : moment(from),
      toDate        : moment(to),
      selectedRadio : preset
    }) as any);
    // the History view runs its report on this rather than on the filter
    // alone, so only a selection let through here is ever queried ...
    setTimeModeReportStale(false);
    acceptHistoryReport();
  }, []);

  // the assets are counted again on Apply, over the range actually chosen -
  // the presets the panel offers are capped already, but a custom range is
  // not, and the map may have changed since the panel opened. A range that
  // exceeds the limit is refused and reported in the panel's place, with
  // nothing applied and any running report left as it was ...
  const onHistoryApply = useCallback(async({preset, from, to}) => {
    const assets = await getFleetAssets().then(countAssets).catch(() => 0);
    const days   = toRangeDays(from, to);
    if (!isVicinityApplied && assets * days > TIME_MODE_ASSET_DAY_LIMIT) {
      setRejectedHistory({selection: {preset, from, to}, assets, days});
    } else {
      applyHistory({preset, from, to});
    }
  }, [getFleetAssets, applyHistory, isVicinityApplied]);

  // which report is running, if either - read off the mode as much as the
  // flags, since the Live History widget runs on the same flags in Live and
  // is not a timeline of the control's ...
  const runningReport = getRunningTimeModeReport({
    timeMode,
    isLiveHistoryActive,
    isHistoryActive,
    trackingMode
  });

  // a running report follows the filter: changing what is on the map re-runs
  // it over the assets now there - whether the change came from the tracking
  // filter, its chips or the grid's own filter, which the fleet clause reads
  // alike - unless those exceed the limit over the days it spans, in which
  // case it is left showing what it showed and the refusal is reported
  // instead. A count that fails leaves the report as it was.
  //
  // A Timeline or History panel open at the time is simply put away, nothing
  // applied and nothing reported - the selection it was drafting was made
  // against assets no longer on the map, and whatever the map was in before
  // it carries on. Asking for the mode again checks it afresh ...
  useEffect(() => {
    let isCancelled = false;
    const hasChangedFilters = previousReportScopeRef.current !== reportScope;
    previousReportScopeRef.current = reportScope;
    if (hasChangedFilters && isModeRequested) {
      dispatch(setTimeModePanelOpen(false));
    } else if (hasChangedFilters && runningReport && !isPanelOpen) {
      getFleetAssets()
        .then(function(features) {
          if (!isCancelled) {
            const assets = countAssets(features);
            const days   = runningReport === TimeModes.timeline
              ? TIMELINE_LIMIT_DAYS
              : toRangeDays(historyFrom as Date, historyTo as Date);
            // a filter that leaves no assets, or more than the limit allows,
            // is refused - the report keeps showing what it shows. A History
            // report over a vicinity search is never held to the limit ...
            const isOverLimit = assets * days > TIME_MODE_ASSET_DAY_LIMIT &&
              !(runningReport === TimeModes.history && isVicinityApplied);
            if (!assets || isOverLimit) {
              setStaleReport({mode: runningReport, assets, days});
              setTimeModeReportStale(true);
            } else if (runningReport === TimeModes.timeline) {
              setStaleReport(null);
              setTimeModeReportStale(false);
              runTimeline(features);
            } else {
              setStaleReport(null);
              setTimeModeReportStale(false);
              dispatch(applyAssetHistoryDateFilter({
                fromDate      : moment(historyFrom),
                toDate        : moment(historyTo),
                selectedRadio : historyPreset
              }) as any);
              acceptHistoryReport();
            }
          }
        })
        .catch(function() {
          setStaleReport(null);
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [reportScope]);

  // a refusal only answers for the report it was raised over - and a History
  // report a vicinity search has since been applied over is no longer held
  // to the limit it was refused against ...
  useEffect(() => {
    if (!runningReport || (runningReport === TimeModes.history && isVicinityApplied)) {
      setStaleReport(null);
      setTimeModeReportStale(false);
    }
  }, [runningReport, isVicinityApplied]);

  // Return to Live tears the report all the way down, the same way the
  // control's own Live pill does ...
  // shared with a refused reopen of the mode already running, whose panel
  // request is put away with it ...
  const onStaleReturnToLive = useCallback(() => {
    setStaleReport(null);
    dispatch(setTimeModePanelOpen(false));
    returnToLiveTracking();
  }, [returnToLiveTracking]);

  // the X leaves the report showing its previous results ...
  const onStaleDismiss = useCallback(() => {
    setStaleReport(null);
  }, []);

  // Change Date Range puts the History panel up over a History report the
  // filter change was refused for, on the range it is running over - a
  // shorter one may let the new filter through, which the panel counts
  // afresh against the assets now on the map ...
  const onStaleChangeDateRange = useCallback(() => {
    setStaleReport(null);
    dispatch(setTimeModePanelOpen(true));
  }, []);

  // Refine Filter hands the user back to the filter, the report still
  // running on its previous results until the filter lets it through ...
  const onStaleRefineFilter = useCallback(() => {
    setStaleReport(null);
    openTrackingFilterAtCategory(TrackingFilterDrawerCategoryId.GroupsAssets);
  }, []);

  // whatever a timeline (or an applied History selection) left behind is
  // torn down when the map goes back to Live, by whichever route it is
  // asked for - the control's own Live pill as much as the panel's Cancel,
  // as much as switching accounts mid-report, which is why this answers
  // the mode rather than sitting inside any one handler.
  //
  // It is the move into Live that is answered, not the mode reading Live.
  // The page opens in Live with nothing to tear down, and the Live History
  // widget runs in Live too - so tearing down on the reading alone would
  // clear that widget the moment the user opened it, and again on mount
  // for nothing. Only the mode is watched: the selected widget is read
  // where the move happens, and a change of its own is not a move ...
  useEffect(() => {
    const wasTimeline     = previousTimeModeRef.current === TimeModes.timeline;
    const hasChangedGroup = previousGlobalGroupIdRef.current !== globalGroupId;
    const hasLeftTimeline = wasTimeline && (timeMode !== TimeModes.timeline || hasChangedGroup);
    const hasLeftTimeMode = hasChangedGroup || (
      previousTimeModeRef.current !== TimeModes.live && timeMode === TimeModes.live
    );
    previousTimeModeRef.current = timeMode;
    previousGlobalGroupIdRef.current = globalGroupId;
    if (hasLeftTimeline) {
      pendingInputClearRef.current = true;
    } else if (timeMode === TimeModes.timeline) {
      pendingInputClearRef.current = false;
    }
    if (pendingInputClearRef.current && hasLeftTimeMode) {
      pendingInputClearRef.current = false;
      dispatch(setLiveHistoryInputEvents([]));
    }
    if (hasLeftTimeMode) {
      const currentLayers = layers?.filter(layerId => layerId !== LIVE_HISTORY_LAYER_ID) || [];
      if (!currentLayers.includes(CURRENT_LAYER)) {
        currentLayers.push(CURRENT_LAYER);
      }
      dispatch(setTimeMode(TimeModes.live));
      dispatch(setTrackingMapLayers(currentLayers));
      setTimeModeLayerCount(null);
      dispatch(cancelLiveHistory());
      dispatch(setMapWidgetSelected(null));
      dispatch(setLiveHistoryFetching(false));
      dispatch(setHistoryActive(false));
      if (trackingMode === TrackingMode.History) {
        dispatch(setTrackingMode(TrackingMode.Live) as any);
      }
    }
  }, [timeMode, globalGroupId]);

  const timelineProps = useMemo(() => ({
    preset   : timelinePreset,
    viewBy   : toViewBy(viewByReportType),
    duration : timelineDuration,
    onApply  : onTimelineApply,
    onCancel : onTimelineDismiss,
    onClose  : onTimelineDismiss,
    inputEventList
  }), [
    timelinePreset,
    timelineDuration,
    viewByReportType,
    onTimelineApply,
    onTimelineDismiss,
    inputEventList
  ]);

  // the panel opens on the refused range where Change Date Range brought it
  // back, and on the last applied one otherwise - capped, either way, at
  // the range the assets on the map leave room for ...
  const historyProps = useMemo(() => ({
    preset   : historyDraft?.preset ?? historyPreset,
    from     : historyDraft?.from ?? historyFrom ?? undefined,
    to       : historyDraft?.to ?? historyTo ?? undefined,
    maxDays  : maxHistoryDays,
    onApply  : onHistoryApply,
    onCancel : onHistoryDismiss,
    onClose  : onHistoryDismiss
  }), [
    historyDraft,
    historyPreset,
    historyFrom,
    historyTo,
    maxHistoryDays,
    onHistoryApply,
    onHistoryDismiss
  ]);

  // a refused History range is reported as History not updated, with a way
  // back to its dates; a mode refused on the way in from Live is reported
  // as too many assets, with a way to stay put ...
  const limitErrorProps = useMemo(() => {
    let props;
    if (hasNoAssets) {
      props = {
        error          : TimeModeLimitErrorType.noAssets,
        targetMode     : timeMode as TimeModes.timeline | TimeModes.history,
        assets         : 0,
        max            : TIME_MODE_ASSET_DAY_LIMIT,
        onRefineFilter : onLimitErrorRefineFilter,
        onClose        : onLimitErrorDismiss
      };
    } else if (rejectedHistory) {
      props = {
        error          : TimeModeLimitErrorType.notUpdated,
        targetMode     : TimeModes.history,
        action         : TimeModeLimitAction.changeDateRange,
        assets         : rejectedHistory.assets,
        days           : rejectedHistory.days,
        max            : TIME_MODE_ASSET_DAY_LIMIT,
        onRefineFilter : onLimitErrorRefineFilter,
        onAction       : onChangeDateRange,
        onClose        : onLimitErrorDismiss
      };
    } else if (exceedsLimit && controlMode === timeMode) {
      // reopened over a History report already running, the card counts the
      // range that report is running over - the one the user has selected -
      // rather than the single day the panel was checked against ...
      const isRunningHistory = timeMode === TimeModes.history && runningReport === TimeModes.history;
      props = {
        error          : TimeModeLimitErrorType.notUpdated,
        targetMode     : timeMode as TimeModes.timeline | TimeModes.history,
        action         : TimeModeLimitAction.returnToLive,
        assets         : assetCount as number,
        days           : isRunningHistory ? toRangeDays(historyFrom as Date, historyTo as Date) : minModeDays,
        max            : TIME_MODE_ASSET_DAY_LIMIT,
        onRefineFilter : onLimitErrorRefineFilter,
        onAction       : onStaleReturnToLive,
        onClose        : onLimitErrorDismiss
      };
    } else if (exceedsLimit) {
      props = {
        error          : TimeModeLimitErrorType.tooManyAssets,
        targetMode     : timeMode as TimeModes.timeline | TimeModes.history,
        currentMode    : controlMode,
        action         : TimeModeLimitAction.stay,
        assets         : assetCount as number,
        days           : minModeDays,
        max            : TIME_MODE_ASSET_DAY_LIMIT,
        onRefineFilter : onLimitErrorRefineFilter,
        onAction       : onLimitErrorDismiss,
        onClose        : onLimitErrorDismiss
      };
    } else if (staleReport && !isPanelOpen) {
      // a History report over more than a day can be brought within the
      // limit by shortening its range, so it is offered back its dates
      // rather than a way out to Live - but only where some range still
      // fits: assets already over the limit for a single day cannot be,
      // whatever dates are picked, and are only offered the way out ...
      const canChangeDateRange = staleReport.mode === TimeModes.history &&
        !!staleReport.assets &&
        staleReport.days > 1 &&
        staleReport.assets * HISTORY_MIN_DAYS <= TIME_MODE_ASSET_DAY_LIMIT;
      props = {
        error : staleReport.assets
          ? TimeModeLimitErrorType.notUpdated
          : TimeModeLimitErrorType.noAssets,
        targetMode : staleReport.mode,
        action     : canChangeDateRange
          ? TimeModeLimitAction.changeDateRange
          : TimeModeLimitAction.returnToLive,
        assets         : staleReport.assets,
        days           : staleReport.days,
        max            : TIME_MODE_ASSET_DAY_LIMIT,
        onRefineFilter : onStaleRefineFilter,
        onAction       : canChangeDateRange ? onStaleChangeDateRange : onStaleReturnToLive,
        onClose        : onStaleDismiss
      };
    }
    return props;
  }, [
    hasNoAssets,
    rejectedHistory,
    exceedsLimit,
    staleReport,
    isPanelOpen,
    timeMode,
    controlMode,
    runningReport,
    historyFrom,
    historyTo,
    assetCount,
    minModeDays,
    onChangeDateRange,
    onLimitErrorDismiss,
    onLimitErrorRefineFilter,
    onStaleChangeDateRange,
    onStaleDismiss,
    onStaleRefineFilter,
    onStaleReturnToLive
  ]);

  // the assets a "{{mode}} wasn't updated" card was counted over are the ones
  // the map shows under the applied filters - shared, keyed by those filters,
  // so the Tracking Filter opened on them warns at once rather than waiting
  // on the backend for a count already in hand ...
  useEffect(() => {
    if (limitErrorProps?.error === TimeModeLimitErrorType.notUpdated) {
      setTimeModeLayerCount({
        key    : getTrackingFilterCountKey(globalGroupId, filters),
        assets : limitErrorProps.assets
      });
    }
  }, [limitErrorProps]);

  if (isMounted) {
    return (
      <DynamicTimeModePanelContainer view={view}
        timeMode={timeMode}
        isPanelOpen={isPanelOpen && !isAwaitingCount && !limitErrorProps}
        timelineProps={timelineProps}
        historyProps={historyProps}
        limitErrorProps={limitErrorProps} />
    );
  }
  return null;
};

export default TimeModePanelContainer;
